package com.videoanalyzer.web.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * URL input form for video analysis.
 *
 * ANDROID EQUIVALENT: An EditText with a Button, like a search bar.
 */
@Composable
fun AnalyzerForm(
    isLoading: Boolean,
    error: String?,
    onAnalyze: (String) -> Unit
) {
    var url by remember { mutableStateOf("") }
    var localError by remember { mutableStateOf<String?>(null) }
    var isFocused by remember { mutableStateOf(false) }

    // Validate URL as user types
    val isValidUrl = url.isNotBlank() && (
            url.contains("youtube.com/watch") ||
                    url.contains("youtu.be/") ||
                    url.contains("youtube.com/shorts/")
            )

    Div({
        classes("card", "fade-in-up")
        style {
            maxWidth(640.px)
            width(100.percent)
            margin(0.px, "auto".unsafeCast<CSSNumeric>())
            padding(28.px)
            property("border", if (isFocused) "1px solid #FF0000" else "1px solid rgba(255, 255, 255, 0.08)")
            property("box-shadow", if (isFocused) "0 0 30px rgba(255, 0, 0, 0.15)" else "none")
            property("transition", "all 0.3s ease")
        }
    }) {
        // Icon and Title
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
                marginBottom(8.px)
            }
        }) {
            // Simple icon - no animation
            Div({
                style {
                    fontSize(28.px)
                }
            }) { Text("🎯") }

            Div {
                H2({
                    style {
                        fontSize(18.px)
                        fontWeight(600)
                        color(Color("#FAFAFA"))
                        marginBottom(2.px)
                    }
                }) { Text("Analyze Your Video") }

                P({
                    style {
                        color(Color("#A1A1AA"))
                        fontSize(13.px)
                        margin(0.px)
                    }
                }) {
                    Text("Get AI-powered insights on your title and thumbnail")
                }
            }
        }

        // Form
        Form(
            action = null,
            attrs = {
                style {
                    marginTop(24.px)
                }
                onSubmit { event ->
                    event.preventDefault()
                    if (isValidUrl && !isLoading) {
                        localError = null
                        onAnalyze(url)
                    } else if (!isValidUrl) {
                        localError = "Please enter a valid YouTube URL"
                    }
                }
            }
        ) {
            // URL input container with glow effect
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(12.px)
                    marginBottom(16.px)
                    position(Position.Relative)
                }
            }) {
                // Input field with icon
                Div({
                    style {
                        property("flex", "1")
                        position(Position.Relative)
                    }
                }) {
                    // YouTube play icon inside input
                    Div({
                        style {
                            position(Position.Absolute)
                            left(16.px)
                            top(50.percent)
                            property("transform", "translateY(-50%)")
                            color(Color("#FF0000"))
                            fontSize(20.px)
                            opacity(if (isFocused) 1 else 0.6)
                            property("transition", "opacity 0.2s ease")
                        }
                    }) { Text("▶") }

                    Input(InputType.Text) {
                        classes("input", "input-large")
                        style {
                            width(100.percent)
                            paddingLeft(48.px)
                            paddingRight(16.px)
                            fontSize(15.px)
                            property("transition", "all 0.3s ease")
                        }
                        placeholder("Paste your YouTube video URL here...")
                        value(url)
                        onInput { event ->
                            url = event.value
                            localError = null
                        }
                        onFocus { isFocused = true }
                        onBlur { isFocused = false }
                        if (isLoading) disabled()
                    }

                    // Valid indicator
                    if (url.isNotBlank()) {
                        Div({
                            style {
                                position(Position.Absolute)
                                right(16.px)
                                top(50.percent)
                                property("transform", "translateY(-50%)")
                                fontSize(18.px)
                                property("transition", "all 0.2s ease")
                            }
                        }) {
                            Text(if (isValidUrl) "✓" else "")
                        }
                    }
                }

                // Analyze button - YouTube red with glow
                Button({
                    classes("btn", "btn-primary", "btn-large")
                    style {
                        minWidth(130.px)
                        fontWeight(600)
                        borderRadius(10.px)
                    }
                    if (!isValidUrl || isLoading) {
                        disabled()
                    }
                }) {
                    if (isLoading) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(8.px)
                            }
                        }) {
                            Div({ classes("spinner")
                                style {
                                    width(18.px)
                                    height(18.px)
                                    property("border-width", "2px")
                                }
                            })
                            Text("Analyzing...")
                        }
                    } else {
                        Span({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(8.px)
                            }
                        }) {
                            Text("Analyze")
                            Span({ style { fontSize(16.px) } }) { Text("→") }
                        }
                    }
                }
            }

            // Error message
            val displayError = localError ?: error
            if (displayError != null) {
                Div({
                    classes("fade-in")
                    style {
                        color(Color("#EF4444"))
                        fontSize(13.px)
                        marginBottom(12.px)
                        padding(10.px, 12.px)
                        backgroundColor(Color("rgba(239, 68, 68, 0.1)"))
                        borderRadius(6.px)
                        property("border-left", "3px solid #EF4444")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                    }
                }) {
                    Span({ style { fontSize(14.px) } }) { Text("⚠") }
                    Text(displayError)
                }
            }
        }

        // Supported URLs hint
        Div({
            style {
                marginTop(16.px)
                padding(12.px)
                backgroundColor(Color("rgba(255, 255, 255, 0.03)"))
                borderRadius(8.px)
                property("border", "1px solid rgba(255, 255, 255, 0.05)")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                    marginBottom(8.px)
                }
            }) {
                Span({ style { fontSize(12.px) } }) { Text("💡") }
                Span({
                    style {
                        fontSize(12.px)
                        color(Color("#A1A1AA"))
                        fontWeight(500)
                    }
                }) { Text("Supported formats") }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexWrap(FlexWrap.Wrap)
                    gap(6.px)
                }
            }) {
                FormatBadge("youtube.com/watch?v=...")
                FormatBadge("youtu.be/...")
                FormatBadge("youtube.com/shorts/...")
            }
        }
    }
}

@Composable
private fun FormatBadge(text: String) {
    Span({
        style {
            fontSize(11.px)
            color(Color("#A1A1AA"))
            backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
            padding(4.px, 8.px)
            borderRadius(4.px)
            fontFamily("'SF Mono', Monaco, monospace")
        }
    }) { Text(text) }
}

/**
 * Empty state shown when no analysis has been run.
 */
@Composable
fun EmptyAnalysisState() {
    Div({
        classes("fade-in-up")
        style {
            textAlign("center")
            padding(48.px, 20.px)
        }
    }) {
        // Simple icon row - no animations
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                gap(12.px)
                marginBottom(20.px)
            }
        }) {
            Span({ style { fontSize(32.px) } }) { Text("🎬") }
            Span({ style { fontSize(32.px) } }) { Text("✨") }
            Span({ style { fontSize(32.px) } }) { Text("📈") }
        }

        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Color("#FAFAFA"))
                marginBottom(8.px)
            }
        }) { Text("Ready to optimize your videos") }

        P({
            style {
                color(Color("#A1A1AA"))
                maxWidth(400.px)
                margin(0.px, "auto".unsafeCast<CSSNumeric>())
                fontSize(14.px)
                lineHeight("1.5")
            }
        }) {
            Text("Paste a YouTube video URL above to get AI-powered insights that help you create better titles and thumbnails.")
        }

        // Feature highlights
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                gap(24.px)
                marginTop(32.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            FeatureHighlight("🎯", "Title Analysis", "Optimize for clicks")
            FeatureHighlight("🖼️", "Thumbnail Review", "Visual feedback")
            FeatureHighlight("💡", "AI Suggestions", "Actionable tips")
        }
    }
}

@Composable
private fun FeatureHighlight(icon: String, title: String, subtitle: String) {
    Div({
        style {
            textAlign("center")
            padding(12.px)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                borderRadius(10.px)
                backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                margin(0.px, "auto".unsafeCast<CSSNumeric>(), 8.px, "auto".unsafeCast<CSSNumeric>())
                fontSize(18.px)
            }
        }) { Text(icon) }

        Div({
            style {
                fontWeight(600)
                color(Color("#FAFAFA"))
                fontSize(13.px)
                marginBottom(2.px)
            }
        }) { Text(title) }

        Div({
            style {
                color(Color("#71717A"))
                fontSize(11.px)
            }
        }) { Text(subtitle) }
    }
}
